import { useEffect, useRef } from 'react';
import { ZZ_OPEN_LABEL } from './opening';
import { ZuzuStack, ChevronLeft, Home, Layers, Sparkle, QR, User, Arrow } from './icons';
import { ZZ_SITE_PATHS, ZZ_MODALITIES, ZZ_EVENT_LINKS } from './data';
import { __ZZ_INITIALS } from './zzHelpers';

/* ZUZU mobile site navigation.
   Shows on narrow viewports only (CSS hides on desktop).
   Two pieces:
   - SMobileTabBar: sticky bottom tab bar (home / rooms / events / pricing / visit)
   - SMobileMenu: full-screen drawer triggered by the hamburger button
   - SMobileMenuButton: the hamburger itself (sits inside .nav .actions)
*/

const ICON_MENU = (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="2" strokeLinecap="round">
    <path d="M3 7h18M3 12h18M3 17h18"/>
  </svg>
);
const ICON_CLOSE = (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="2" strokeLinecap="round">
    <path d="M6 6l12 12M6 18L18 6"/>
  </svg>
);

/* ─── Hamburger button (lives inside the desktop nav row) ─── */
export const SMobileMenuButton = ({ onOpen }) => (
  <button className="nav-hamburger" aria-label="open menu" onClick={onOpen}>
    {ICON_MENU}
  </button>
);

/* ─── Full-screen menu drawer ───────────────────────────── */
export const SMobileMenu = ({ open, onClose, page, onNav, onApp, section }) => {
  const eventsRef = useRef(null);
  // lock body scroll while open
  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  // Opened from the "events" tab → bring the occasion list into view, so the
  // tab lands on what it advertises rather than the top of the menu. The main
  // pages stay one scroll up.
  useEffect(() => {
    if (!open || section !== 'events') return;
    const el = eventsRef.current;
    if (el && typeof el.scrollIntoView === 'function') el.scrollIntoView({ block: 'start' });
  }, [open, section]);

  const go = (p) => { onNav(p); onClose(); };
  const goApp = () => { onApp(); onClose(); };

  return (
    <div className={'mobile-menu' + (open ? ' open' : '')} aria-hidden={!open} inert={!open ? '' : undefined}>
      <div className="mobile-menu-bg" onClick={onClose} />
      <div className="mobile-menu-panel" role="dialog" aria-modal="true">
        <div className="mobile-menu-top">
          <ZuzuStack size={32} />
          <button className="mobile-menu-close" aria-label="close menu" onClick={onClose}>
            {ICON_CLOSE}
          </button>
        </div>
        <nav className="mobile-menu-links">
          <a className={page === 'home' ? 'active' : ''} href={ZZ_SITE_PATHS.home} onClick={(e) => { e.preventDefault(); go('home'); }}>
            <span className="n">01</span> home
          </a>
          <a className={(page === 'modalities' || (typeof page === 'string' && page.startsWith('modality:'))) ? 'active' : ''}
             href={ZZ_SITE_PATHS.modalities} onClick={(e) => { e.preventDefault(); go('modalities'); }}>
            <span className="n">02</span> rooms
          </a>
          <a className={page === 'book' ? 'active' : ''} href={ZZ_SITE_PATHS.book} onClick={(e) => { e.preventDefault(); go('book'); }}>
            <span className="n">03</span> book
          </a>
          <a className={page === 'pricing' ? 'active' : ''} href={ZZ_SITE_PATHS.pricing} onClick={(e) => { e.preventDefault(); go('pricing'); }}>
            <span className="n">04</span> pricing
          </a>
          <a className={page === 'about' ? 'active' : ''} href={ZZ_SITE_PATHS.about} onClick={(e) => { e.preventDefault(); go('about'); }}>
            <span className="n">05</span> about
          </a>
          <a className={page === 'contact' ? 'active' : ''} href={ZZ_SITE_PATHS.contact} onClick={(e) => { e.preventDefault(); go('contact'); }}>
            <span className="n">06</span> visit
          </a>
          <div className="mobile-menu-eyebrow">the five rooms</div>
          {(ZZ_MODALITIES || []).map((m, i) =>
          <a key={m.id} className={'mobile-menu-sub' + (page === 'modality:' + m.id ? ' active' : '')}
          href={ZZ_SITE_PATHS['modality:' + m.id] || ZZ_SITE_PATHS.modalities}
          onClick={(e) => { e.preventDefault(); go('modality:' + m.id); }}>
            <span className="n">{String(7 + i).padStart(2, '0')}</span> {m.name}
            {m.temp && <span className="d">{m.temp}</span>}
          </a>
          )}
          {/* Mirrors the desktop "events" dropdown — unnumbered, because the
              list grows as occasion pages are added and hand-numbering it
              would drift. */}
          <div className="mobile-menu-eyebrow" ref={eventsRef}>events &amp; groups</div>
          {ZZ_EVENT_LINKS.map((ev) =>
          <a key={ev.id} className="mobile-menu-sub"
          href={ZZ_SITE_PATHS[ev.id]}
          onClick={(e) => { e.preventDefault(); go(ev.id); }}>
            {ev.label}
          </a>
          )}
        </nav>
        <div className="mobile-menu-foot">
          <button className="btn btn--primary" onClick={() => go('signup:monthly')}>
            become a member <Arrow />
          </button>
          <button className="btn btn--ghost" onClick={goApp}>
            open the app
          </button>
          <div className="mobile-menu-meta">
            <div>
              <div className="k">status</div>
              <div className="v">{ZZ_OPEN_LABEL}</div>
            </div>
            <div>
              <div className="k">find us</div>
              <div className="v">century city</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ─── Marketing tab icons ───────────────────────────────── */
const M_ICON_HOME = <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M3 11l9-8 9 8v9a2 2 0 0 1-2 2h-4v-7h-6v7H5a2 2 0 0 1-2-2v-9z"/></svg>;
const M_ICON_ROOMS = <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3 2 8l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/></svg>;
const M_ICON_PRICING = <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9"/><path d="M9 9.5a2.5 2.5 0 0 1 5 0c0 1.5-2.5 2-2.5 3.5M12 17h.01"/></svg>;
const M_ICON_EVENTS = <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M4.5 20.5 9 9l6 6-11.5 4.5a.7.7 0 0 1-.9-.9z"/><path d="M13 3.5v2M18.5 6l-1.4 1.4M20.5 12h-2"/><path d="M11.5 6.5a3 3 0 0 0 3 3 3 3 0 0 0 3 3"/></svg>;
const M_ICON_VISIT = <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>;

/* ─── Page title + back-target helpers (drive the app header) ─── */
const M_TITLES = { modalities: 'rooms', book: 'book a slot', pricing: 'pricing', about: 'about', contact: 'visit' };
export const mBackTarget = (page) => {
  if (typeof page === 'string' && page.startsWith('modality:')) return 'modalities';
  if (page == null || page === 'home') return null;
  return 'home';
};
const mTitle = (page) => {
  if (typeof page === 'string' && page.startsWith('modality:')) {
    const m = (ZZ_MODALITIES || []).find((x) => x.id === page.slice('modality:'.length));
    return m ? m.name : 'room';
  }
  return M_TITLES[page] || '';
};

/* ─── App-style sticky header (mobile only; hidden on desktop) ─── */
export const SMobileAppHeader = ({ page, onNav, onApp, onAuth, onBack, onOpenMenu, loggedIn }) => {
  const back = mBackTarget(page);
  const title = mTitle(page);
  // The logo doubles as the burger: tapping it opens the full nav drawer
  // (home lives in there). On sub-pages the slot shows back instead — the
  // drawer stays reachable from any root page and the bottom tabs.
  const brand = (
    <button className="m-appbar-brand m-appbar-brand--menu" onClick={onOpenMenu} aria-label="open menu" aria-haspopup="dialog">
      <ZuzuStack size={24} />
      <span className="m-appbar-burger" aria-hidden="true">{ICON_MENU}</span>
    </button>
  );
  return (
    <header className="m-appbar">
      <div className="m-appbar-side">
        {back ? (
          <button className="m-appbar-btn" aria-label="back" onClick={() => onBack(back)}>
            <ChevronLeft size={20} />
          </button>
        ) : brand}
      </div>
      <div className="m-appbar-title">{title}</div>
      <div className="m-appbar-side m-appbar-side--right">
        {loggedIn ? (
          <button className="m-appbar-avatar" onClick={() => onApp()} aria-label="your account">{__ZZ_INITIALS()}</button>
        ) : (
          <button className="m-appbar-signin" onClick={onAuth}>sign in</button>
        )}
      </div>
    </header>
  );
};

/* ─── Bottom tab bar (always rendered, hidden on desktop) ───
   Logged out → marketing tabs. Logged in → app/booking tabs that
   drop you straight into the signed-in app surface. */
export const SMobileTabBar = ({ page, onNav, onApp, onScreen, view, onView, loggedIn, onOpenMenu }) => {
  const isApp = view === 'app';
  const isModalityPage = typeof page === 'string' && page.startsWith('modality:');
  const goApp = (s) => { onView('app'); onScreen(s); };
  const TABS = loggedIn ? [
    { id: 'today',   label: 'today',  icon: <Home size={20} />,    active: isApp && view === 'app' && page === 'today',  onClick: () => goApp('today') },
    { id: 'browse',  label: 'rooms',  icon: <Layers size={20} />,  active: false, onClick: () => goApp('browse') },
    { id: 'stack',   label: 'book',   icon: <Sparkle size={20} />, active: false, onClick: () => goApp('stack') },
    { id: 'wallet',  label: 'wallet', icon: <QR size={20} />,      active: false, onClick: () => goApp('wallet') },
    { id: 'profile', label: 'you',    icon: <User size={20} />,    active: false, onClick: () => goApp('profile') },
  ] : [
    { id: 'home',    label: 'home',    icon: M_ICON_HOME,    active: !isApp && page === 'home',     onClick: () => onNav('home') },
    { id: 'rooms',   label: 'rooms',   icon: M_ICON_ROOMS,   active: !isApp && (page === 'modalities' || isModalityPage), onClick: () => onNav('modalities') },
    // Mirrors the desktop "events" dropdown. The occasion pages are their own
    // top-level routes (not pages of this shell), so there's no single events
    // page to tab to — the tab opens the drawer at the occasion list, which is
    // what hovering the desktop trigger reveals.
    { id: 'events',  label: 'events',  icon: M_ICON_EVENTS,  active: false, onClick: () => onOpenMenu && onOpenMenu('events') },
    { id: 'pricing', label: 'pricing', icon: M_ICON_PRICING, active: !isApp && page === 'pricing',  onClick: () => onNav('pricing') },
    { id: 'visit',   label: 'visit',   icon: M_ICON_VISIT,   active: !isApp && page === 'contact',  onClick: () => onNav('contact') },
  ];
  return (
    <div className={'mobile-tabbar' + (loggedIn ? ' mobile-tabbar--app' : '')} role="navigation" aria-label="primary">
      {TABS.map((t) => (
        <button key={t.id} className={t.active ? 'active' : ''} onClick={t.onClick} aria-label={t.label}>
          <span className="icon">{t.icon}</span>
          <span className="lbl">{t.label}</span>
        </button>
      ))}
    </div>
  );
};
